import React, { useEffect, useRef, useState } from "react";
import { CloseOutlined, FilterOutlined, SearchOutlined } from '@ant-design/icons';
import parse from 'html-react-parser';
import data from './benefits.json';
import rollover25k from './assets/image-1.jpg';
import rollover8k from './assets/image-2.jpg';
import deluxeRoom from './assets/image-3.jpg';
import suite from './assets/image-4.jpg';
import banyan from './assets/image-5.svg';
import rihga from './assets/image-6.png';
import './style.scss';

const tiers = [
  { id: 'member', title: 'Member', name: '会员' },
  { id: 'loyal', title: 'Loyal', name: '尊贵会员', nights: '10晚以上住宿　或', points: '累积7,000积分以上' },
  { id: 'exclusive', title: 'Exclusive', name: '至尊会员', nights: '30晚以上住宿　或', points: '累积20,000积分以上' },
  { id: 'exclusive-plus', title: 'Exclusive Plus', name: '至尊Plus会员', nights: '60晚以上住宿　或', points: '累积40,000积分以上' },
];
const images = {
  'image-1.jpg': rollover25k, 'image-2.jpg': rollover8k,
  'image-3.jpg': deluxeRoom, 'image-4.jpg': suite,
  'image-5.svg': banyan, 'image-6.png': rihga,
};

function TierIcon({ tier }) {
  return <span className={`oh-tier-icon oh-tier-icon--${tier.id}`} role="img" aria-label={tier.name} />;
}

// Only the checked-in, link-free official content is parsed; no remote HTML is loaded.
function RichText({ html }) {
  return parse(html || '', {
    replace: node => {
      if (node.name === 'img') {
        return <img src={images[node.attribs.src]} alt={node.attribs.alt || ''} loading="lazy" />;
      }
      if (node.name === 'i') {
        const tier = tiers.find(item => (node.attribs.class || '').split(' ').includes(`icon-${item.id}`));
        return tier ? <TierIcon tier={tier} /> : <></>;
      }
    },
  });
}

function HotelTable({ table }) {
  const [query, setQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState({ location: '', brand: '', discount: '' });
  const [draft, setDraft] = useState(filters);
  const filtered = table.groups.map(group => ({
    ...group,
    rows: group.rows.filter(row => (
      (!filters.location || filters.location.split(',').includes(group.location)) &&
      (!filters.brand || group.brand === filters.brand) &&
      (!filters.discount || row.discount === filters.discount) &&
      `${group.title} ${row.search}`.toLowerCase().includes(query.trim().toLowerCase())
    )),
  })).filter(group => group.rows.length);
  const resetFilters = () => {
    const empty = { location: '', brand: '', discount: '' };
    setFilters(empty);
    setDraft(empty);
    setQuery('');
  };

  return (
    <section className="oh-hotel-table" aria-label={table.title}>
      <h3>{table.title}</h3>
      <div className="oh-search">
        <SearchOutlined aria-hidden="true" />
        <input type="search" value={query} onChange={event => setQuery(event.target.value)} placeholder="输入文字进行搜索" aria-label="搜索酒店、餐厅或客房" />
        <button type="button" aria-expanded={filterOpen} aria-controls="oh-filters" onClick={() => { setDraft(filters); setFilterOpen(!filterOpen); }}>
          <FilterOutlined aria-hidden="true" />筛选
        </button>
      </div>
      {filterOpen && (
        <form id="oh-filters" className="oh-filters" onSubmit={event => { event.preventDefault(); setFilters(draft); setFilterOpen(false); }}>
          {Object.entries({ location: '目的地', brand: '品牌', discount: '优惠房价' }).map(([key, label]) => table.filters[key].length > 0 && (
            <label key={key}>{label}
              <select aria-label={label} value={draft[key]} onChange={event => setDraft({ ...draft, [key]: event.target.value })}>
                <option value="">全部</option>
                {table.filters[key].map(option => <option key={`${option.value}-${option.label}`} value={option.value}>{option.label}</option>)}
              </select>
            </label>
          ))}
          <div className="oh-filter-actions">
            <button type="button" onClick={resetFilters}>全部清除</button>
            <button type="submit">应用筛选</button>
          </div>
        </form>
      )}
      <div className="oh-filter-status" aria-live="polite">
        {(query || Object.values(filters).some(Boolean)) && <button type="button" onClick={resetFilters}>全部清除</button>}
        {!filtered.length && <p>未找到符合条件的结果</p>}
      </div>
      <div className="oh-hotel-columns" style={{ '--oh-columns': table.columns.length }}>
        {table.columns.map((column, index) => <span key={index}>{column}</span>)}
      </div>
      {filtered.map((group, index) => (
        <section className="oh-hotel-group" key={`${group.title}-${index}`} aria-label={group.title}>
          <h4>{group.title}</h4>
          {group.rows.map((row, rowIndex) => (
            <div className={`oh-hotel-row oh-hotel-row--${row.cells.length}`} key={rowIndex} style={{ '--oh-columns': row.cells.length }}>
              {row.cells.map((cell, cellIndex) => <div key={cellIndex}><RichText html={cell} /></div>)}
            </div>
          ))}
        </section>
      ))}
    </section>
  );
}

function BenefitDialog({ benefit, onClose, scrollRoot }) {
  const dialogRef = useRef(null);
  const closeRef = useRef(null);

  useEffect(() => {
    const trigger = document.activeElement;
    const root = scrollRoot.current.closest('.container') || document.body;
    const previousOverflow = root.style.overflow;
    root.style.overflow = 'hidden';
    closeRef.current.focus();
    return () => {
      root.style.overflow = previousOverflow;
      if (trigger && trigger.isConnected) trigger.focus({ preventScroll: true });
    };
  }, [scrollRoot]);

  const handleKeyDown = event => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
    }
    if (event.key === 'Tab') {
      const elements = [...dialogRef.current.querySelectorAll('button, input, select, summary')].filter(el => el.getClientRects().length && !el.disabled);
      const first = elements[0];
      const last = elements[elements.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }
  };

  return (
    <div className="oh-dialog-backdrop" onClick={event => { if (event.target === event.currentTarget) onClose(); }}>
      <section className="oh-dialog" role="dialog" aria-modal="true" aria-labelledby="oh-dialog-title" ref={dialogRef} onKeyDown={handleKeyDown}>
        <header className="oh-dialog-head">
          <h2 id="oh-dialog-title">{benefit.title}</h2>
          <button type="button" aria-label="关闭权益详情" title="关闭权益详情" ref={closeRef} onClick={onClose}><CloseOutlined /></button>
        </header>
        <div className="oh-dialog-body">
          <RichText html={benefit.body} />
          {benefit.steps?.length > 0 && <section className="oh-detail-section"><h3>使用方法</h3><ol>{benefit.steps.map(step => <li key={step}>{step}</li>)}</ol></section>}
          {benefit.offers && <section className="oh-detail-section"><RichText html={benefit.offers} /></section>}
          {benefit.example && <section className="oh-detail-section oh-examples"><h3>{benefit.example.title}</h3><p>{benefit.example.description}</p>{benefit.example.images.map(img => <img key={img.src} src={images[img.src]} alt={img.alt} loading="lazy" width="342" height="289" />)}</section>}
          {benefit.table && <HotelTable table={benefit.table} />}
          {benefit.brands && <section className="oh-detail-section oh-brands"><h3>查找酒店</h3>{benefit.brands.map(brand => <details key={brand.title} open><summary>{brand.title}</summary><ul>{brand.hotels.map(hotel => <li key={hotel}>{hotel}</li>)}</ul></details>)}</section>}
          {benefit.partners && <section className="oh-detail-section"><RichText html={benefit.partners} /></section>}
        </div>
      </section>
    </div>
  );
}

export default function OneHarmonyBenefits() {
  const [selected, setSelected] = useState(null);
  const rootRef = useRef(null);

  return (
    <main  ref={rootRef}>
      
      <h1 className="hotel-member-title">
        <img className="hotel-member-logo harmony-logo" src="https://staticfile.badazhou.com/20260922/570faec501b8cfc352fbd4795ebae96d.png" alt="" />
        <span>One Harmony-会员礼遇</span>
      </h1>

      <div className="one-harmony" aria-hidden={selected ? 'true' : undefined}>
       
        <section className="oh-membership" aria-labelledby="oh-membership-title">
          <header className="oh-membership-intro">
            <h2 id="oh-membership-title">会员等级</h2>
            <p>从您加入会员的当天起即可累积积分，尽享多种专属礼遇。</p>
          </header>
          {tiers.map(tier => (
            <div className="oh-membership-row" key={tier.id}>
              <div className="oh-membership-requirements"><p>{tier.nights}</p><p>{tier.points}</p></div>
              <div className={`oh-membership-card oh-membership-card--${tier.id}`}>
                <div className="oh-membership-card-title">{tier.title}</div>
                <div className="oh-membership-card-name">{tier.name}</div>
              </div>
            </div>
          ))}
        </section>
        <section className="oh-benefits" aria-labelledby="oh-benefits-title">
          <header className="oh-sticky-header">
            <h2 id="oh-benefits-title">会员专属礼遇</h2>
            <div className="oh-tier-headings">{tiers.map(tier => <span key={tier.id}>{tier.name}</span>)}</div>
          </header>
          {data.groups.map(group => (
            <section className="oh-benefit-group" key={group.title} aria-label={group.title}>
              <h3>{group.title}</h3>
              {group.benefits.map(benefit => (
                <div className="oh-benefit-row" key={benefit.id}>
                  <button type="button" className="oh-benefit-link" aria-haspopup="dialog" onClick={() => setSelected(benefit)}>{benefit.title}</button>
                  <div className="oh-entitlements">{benefit.values.map((value, index) => <div key={tiers[index].id} aria-label={`${tiers[index].name}：${value === true ? '享有礼遇' : value}`}>
                    {value === true ? <TierIcon tier={tiers[index]} /> : value}
                  </div>)}</div>
                </div>
              ))}
            </section>
          ))}
        </section>
      </div>
      {selected && <BenefitDialog key={selected.id} benefit={selected} onClose={() => setSelected(null)} scrollRoot={rootRef} />}
    </main>
  );
}
